import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService, isUnauthorized } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { StorageService } from 'app/core/storage/storage.service';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

/** Adresy bez sesji: logowanie i hasło, „Zapytaj o dostęp”, kontakt biura. */
const PUBLIC_API = ['/api/v1/auth/', '/api/v1/access-requests', '/api/v1/public/'];

const isApi = (url: string): boolean => url.includes('/api/v1/');
const isPublicApi = (url: string): boolean => PUBLIC_API.some((path) => url.includes(path));

/**
 * Token dostępu w żądaniach API (Issue 42). Wygasły token (np. po uśpieniu komputera) odnawiamy przed wysłaniem;
 * 401 z API → jedno ciche odnowienie i powtórzenie żądania. Dopiero odrzucone odnowienie kończy sesję ekranem
 * „Sesja wygasła” z powrotem na tę samą stronę — zamiast dawnego czyszczenia danych i przeładowania przy każdym 401.
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    if (!isApi(req.url) || isPublicApi(req.url)) {
        return next(req);
    }

    const auth = inject(AuthService);
    const storage = inject(StorageService);
    const send = (token: string | null) =>
        next(token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req);
    const endSession = (error: unknown) => {
        if (isUnauthorized(error) && auth.onProtectedPage()) {
            auth.expire('expired');
        }

        return throwError(() => error);
    };
    const refreshAndSend = () =>
        auth.refresh().pipe(
            catchError(endSession),
            switchMap((token) => send(token).pipe(catchError(endSession)))
        );

    const access = storage.accessToken || null;

    if (access && AuthUtils.isTokenExpired(access, 5) && storage.refreshToken) {
        return refreshAndSend();
    }

    return send(access).pipe(
        catchError((error) => {
            if (!isUnauthorized(error)) {
                return throwError(() => error);
            }

            if (storage.refreshToken) {
                return refreshAndSend();
            }

            return access ? endSession(error) : throwError(() => error);
        })
    );
};
